'use client';
import React, { useState, useRef, useCallback } from 'react';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function getFileTypeLabel(file: File): string {
  const ext = file.name.split('.').pop()?.toUpperCase() ?? '';
  if (ext) return `${ext} Audio`;
  if (file.type) return file.type.replace('audio/', '').toUpperCase() + ' Audio';
  return 'Audio File';
}

type OutputFormat = 'same' | 'mp3' | 'wav';

export default function AudioMetadataRemoverTool() {
  const [audioFile, setAudioFile] = useState<{ file: File; url: string; size: number } | null>(null);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('same');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState(0);
  const [resultExt, setResultExt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadFile = useCallback((file: File) => {
    if (file.size > 500 * 1024 * 1024) { setError('File too large. Max 500 MB.'); return; }
    setError(null); setResultUrl(null); setResultSize(0); setResultExt('');
    setAudioFile({ file, url: URL.createObjectURL(file), size: file.size });
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    const f = e.dataTransfer.files?.[0]; if (f) loadFile(f);
  };

  const handleProcess = async () => {
    if (!audioFile) return;
    setProcessing(true); setProgress(0); setError(null); setResultUrl(null);
    try {
      setProgressLabel('Loading FFmpeg engine…'); setProgress(8);
      const { FFmpeg } = await import('@ffmpeg/ffmpeg');
      const { fetchFile, toBlobURL } = await import('@ffmpeg/util');
      const ffmpeg = new FFmpeg();
      ffmpeg.on('progress', ({ progress: p }) => {
        setProgress(Math.round(20 + p * 70));
        setProgressLabel(`Processing… ${Math.round(p * 100)}%`);
      });
      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      });
      setProgress(20); setProgressLabel('Reading file…');

      const inputExt = audioFile.file.name.split('.').pop()?.toLowerCase() || 'mp3';
      const outExt = outputFormat === 'same' ? inputExt : outputFormat;
      const outputFileName = `output.${outExt}`;

      await ffmpeg.writeFile(`input.${inputExt}`, await fetchFile(audioFile.file));

      setProgressLabel('Stripping metadata…');

      // If same format, use stream copy (no re-encoding). Otherwise re-encode.
      const needsReencode = outputFormat !== 'same' && outExt !== inputExt;
      const ffmpegArgs = needsReencode
        ? ['-i', `input.${inputExt}`, '-map_metadata', '-1', outputFileName]
        : ['-i', `input.${inputExt}`, '-map_metadata', '-1', '-c', 'copy', outputFileName];

      await ffmpeg.exec(ffmpegArgs);

      setProgress(95); setProgressLabel('Preparing download…');
      const data = await ffmpeg.readFile(outputFileName);
      const mimeType = outExt === 'wav' ? 'audio/wav' : outExt === 'mp3' ? 'audio/mpeg' : (audioFile.file.type || 'audio/mpeg');
      const blob = new Blob([data], { type: mimeType });
      setResultUrl(URL.createObjectURL(blob));
      setResultSize(blob.size);
      setResultExt(outExt);
      setProgress(100); setProgressLabel('Done!');
    } catch (err) {
      console.error(err);
      setError('Processing failed. Please try a different file or format.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!resultUrl || !audioFile) return;
    const baseName = audioFile.file.name.replace(/\.[^.]+$/, '');
    const a = document.createElement('a');
    a.href = resultUrl;
    a.download = `clean_${baseName}.${resultExt}`;
    a.click();
  };

  const sizeDiff = audioFile && resultSize ? audioFile.size - resultSize : 0;

  return (
    <div className="space-y-5">
      {/* Privacy message */}
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4">
        <svg className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
        </svg>
        <p className="text-amber-800 text-sm font-medium">Remove hidden metadata before sharing audio files.</p>
      </div>

      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-4">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {!audioFile ? (
        <div
          className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${isDragging ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-gray-50 hover:border-amber-300 hover:bg-amber-50/50'}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <span className="text-4xl block mb-3">🏷️</span>
          <p className="font-semibold text-gray-800 mb-1">Upload Audio File</p>
          <p className="text-gray-500 text-sm mb-3">Drag & drop or click to browse</p>
          <p className="text-gray-400 text-xs">Supports MP3, WAV, OGG, AAC, FLAC, M4A</p>
          <input ref={fileInputRef} type="file" accept=".mp3,.wav,.ogg,.aac,.flac,.m4a" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) loadFile(f); }} />
        </div>
      ) : (
        <div className="space-y-4">
          {/* File info card */}
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🏷️</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 text-sm truncate mb-2">{audioFile.file.name}</p>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-white rounded-lg p-2 text-center border border-amber-100">
                    <p className="text-xs text-gray-500 mb-0.5">File Name</p>
                    <p className="text-xs font-semibold text-gray-700 truncate">{audioFile.file.name.replace(/\.[^.]+$/, '')}</p>
                  </div>
                  <div className="bg-white rounded-lg p-2 text-center border border-amber-100">
                    <p className="text-xs text-gray-500 mb-0.5">File Size</p>
                    <p className="text-xs font-semibold text-gray-700">{formatBytes(audioFile.size)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-2 text-center border border-amber-100">
                    <p className="text-xs text-gray-500 mb-0.5">File Type</p>
                    <p className="text-xs font-semibold text-gray-700">{getFileTypeLabel(audioFile.file)}</p>
                  </div>
                </div>
              </div>
              <button onClick={() => { setAudioFile(null); setResultUrl(null); }} className="text-gray-400 hover:text-red-500 p-1 shrink-0">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>

          {/* Original preview */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Original Preview</p>
            <audio src={audioFile.url} controls className="w-full h-10" />
          </div>

          {/* What will be removed */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
            <p className="text-sm font-semibold text-blue-800 mb-2">What will be removed:</p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-blue-700">
              <li>• ID3 tags (title, artist, album)</li>
              <li>• Album artwork / cover image</li>
              <li>• Comments and lyrics</li>
              <li>• Copyright and encoder info</li>
              <li>• Year, genre, track number</li>
              <li>• All other embedded metadata</li>
            </ul>
          </div>

          {/* Output format selector */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Output Format</p>
            <div className="grid grid-cols-3 gap-2">
              {([
                { value: 'same', label: 'Same Format', desc: 'Keep original' },
                { value: 'mp3', label: 'MP3', desc: 'Widely compatible' },
                { value: 'wav', label: 'WAV', desc: 'Lossless quality' },
              ] as { value: OutputFormat; label: string; desc: string }[]).map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setOutputFormat(opt.value)}
                  className={`rounded-xl border-2 p-3 text-center transition-all ${outputFormat === opt.value ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-white hover:border-amber-200'}`}
                >
                  <p className={`text-sm font-semibold ${outputFormat === opt.value ? 'text-amber-700' : 'text-gray-700'}`}>{opt.label}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Progress bar */}
          {processing && (
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1"><span>{progressLabel}</span><span>{progress}%</span></div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          {/* Result */}
          {resultUrl && (
            <div className="bg-green-50 border border-green-100 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <p className="font-semibold text-green-800 text-sm">Metadata removed successfully!</p>
              </div>

              {/* Before / After size comparison */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-white rounded-lg p-2.5 text-center border border-green-100">
                  <p className="text-xs text-gray-500 mb-0.5">Before</p>
                  <p className="text-sm font-bold text-gray-700">{formatBytes(audioFile.size)}</p>
                </div>
                <div className="bg-white rounded-lg p-2.5 text-center border border-green-100">
                  <p className="text-xs text-gray-500 mb-0.5">After</p>
                  <p className="text-sm font-bold text-green-700">{formatBytes(resultSize)}</p>
                </div>
                <div className="bg-white rounded-lg p-2.5 text-center border border-green-100">
                  <p className="text-xs text-gray-500 mb-0.5">Saved</p>
                  <p className={`text-sm font-bold ${sizeDiff > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                    {sizeDiff > 0 ? `−${formatBytes(sizeDiff)}` : '—'}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Clean Audio Preview</p>
                <audio src={resultUrl} controls className="w-full h-10" />
              </div>
              <button onClick={handleDownload} className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors text-sm">
                ⬇️ Download Clean Audio ({resultExt.toUpperCase()})
              </button>
            </div>
          )}

          {!resultUrl && (
            <button onClick={handleProcess} disabled={processing} className="w-full py-3.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors text-sm">
              {processing ? 'Removing Metadata…' : 'Remove All Metadata'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
